import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiChevronLeft, FiChevronRight, FiX, FiCheck, FiLock, FiTrash2, FiDownload, FiArrowLeft, FiBell, FiTag, FiCopy, FiDollarSign, FiUserPlus, FiPlus, FiFileText } from 'react-icons/fi';
import SyncHealthWidget from '../components/SyncHealthWidget';
import AdminCalRatesPanel from '../components/AdminCalRatesPanel';
import QuoteModal from '../components/quote/QuoteModal';

const API = process.env.REACT_APP_BACKEND_URL;

const VILLAS = [
  { id: '3bhk-garden-escape-lonavala', name: '3BHK', color: '#F57C20', bg: '#FFF7ED' },
  { id: '4bhk-garden-retreat-lonavala', name: '4BHK', color: '#8B5CF6', bg: '#F5F3FF' },
  { id: '5bhk-garden-delight-lonavala', name: '5BHK', color: '#10B981', bg: '#ECFDF5' },
  { id: '12bhk-celebration-villa-lonavala', name: '12BHK', color: '#EF4444', bg: '#FEF2F2' },
];

const getDeviceId = () => {
  let id = localStorage.getItem('rgv_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);
    localStorage.setItem('rgv_device_id', id);
  }
  return id;
};

const toDateStr = (d) => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const AdminCalPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  // For audio ping + previous bookings count tracking (used to detect a NEW arrival)
  const prevBookingsCount = useRef(0);
  const audioRef = useRef(null);
  const [valid, setValid] = useState(null);
  const [error, setError] = useState('');
  const [blockedData, setBlockedData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVilla, setSelectedVilla] = useState('3bhk-garden-escape-lonavala');
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dayDetail, setDayDetail] = useState(null);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [installedPreviously, setInstalledPreviously] = useState(() => {
    try { return localStorage.getItem('rgv_cal_installed') === '1'; }
    catch { return false; }
  });
  const [showIosHint, setShowIosHint] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try { return sessionStorage.getItem('rgv_cal_install_dismissed') === '1'; }
    catch { return false; }
  });
  const [browserAdvice, setBrowserAdvice] = useState(null); // {type: 'inapp'|'samsung'|null, label: 'WhatsApp'|...}
  const [browserAdviceDismissed, setBrowserAdviceDismissed] = useState(() => {
    try { return sessionStorage.getItem('rgv_cal_browser_advice_dismissed') === '1'; }
    catch { return false; }
  });

  // Detect non-Chrome Android browsers where PWA install is broken / not available
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    if (!isAndroid) return;

    // In-app browsers (WhatsApp, Instagram, Facebook, etc.)
    // Android WebView UAs include `; wv)` or `; wv;` or end with ` wv`.
    // Specific in-app browser apps include their own identifier.
    const isWebView = /;\s*wv\b/i.test(ua) || /\bwv\)/i.test(ua);
    const isInAppApp = /FBAN|FBAV|Instagram|Line\/|MicroMessenger|Twitter|Snapchat|Pinterest/i.test(ua);
    const isInApp = isWebView || isInAppApp;
    const isSamsung = /SamsungBrowser/i.test(ua);
    const isChrome = /Chrome\//i.test(ua) && !isInApp && !isSamsung && !/EdgA|OPR|UCBrowser|MiuiBrowser/i.test(ua);

    if (isInApp) {
      let label = 'WhatsApp / in-app';
      if (/FBAN|FBAV/i.test(ua)) label = 'Facebook';
      else if (/Instagram/i.test(ua)) label = 'Instagram';
      else if (/Line\//i.test(ua)) label = 'LINE';
      else if (/MicroMessenger/i.test(ua)) label = 'WeChat';
      else if (/Twitter/i.test(ua)) label = 'Twitter / X';
      setBrowserAdvice({ type: 'inapp', label });
    } else if (isSamsung) {
      setBrowserAdvice({ type: 'samsung', label: 'Samsung Internet' });
    } else if (!isChrome) {
      setBrowserAdvice({ type: 'other', label: 'this browser' });
    }
  }, []);

  const dismissInstall = () => {
    setInstallDismissed(true);
    try { sessionStorage.setItem('rgv_cal_install_dismissed', '1'); } catch { /* noop */ }
  };

  const dismissBrowserAdvice = () => {
    setBrowserAdviceDismissed(true);
    try { sessionStorage.setItem('rgv_cal_browser_advice_dismissed', '1'); } catch { /* noop */ }
  };

  // Re-open current URL inside Chrome via Android Intent URL
  const openInChrome = () => {
    const fullUrl = window.location.href;
    // Strip protocol for the intent host segment
    const noProto = fullUrl.replace(/^https?:\/\//, '');
    const intentUrl =
      `intent://${noProto}#Intent;scheme=https;package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(fullUrl)};end`;
    window.location.href = intentUrl;
  };

  // Register PWA manifest + service worker (must run on first paint for installability)
  useEffect(() => {
    // CRITICAL: Use STATIC manifest URL (not blob URL). Chrome won't fire
    // `beforeinstallprompt` for blob-URL manifests, and blob URLs die when the
    // tab closes (breaks the installed-app icon).
    // We save the current token to localStorage so that when the installed
    // PWA launches at /admin-cal/ (the manifest's start_url), our redirect
    // route can route the user back to /admin-cal/<their-token>.
    try { localStorage.setItem('rgv_cal_last_token', token); } catch (e) { /* noop */ }

    // 1) Manifest link
    let link = document.querySelector('link[rel="manifest"][data-rgv-cal="1"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.setAttribute('data-rgv-cal', '1');
      document.head.appendChild(link);
    }
    link.href = '/admin-cal-manifest-v2.json';

    // 2) Theme color
    let meta = document.querySelector('meta[name="theme-color"][data-rgv-cal="1"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = '#F57C20';
      meta.setAttribute('data-rgv-cal', '1');
      document.head.appendChild(meta);
    }

    // 3) Apple touch icon (PNG required by iOS)
    // Use the 180px variant — iOS spec requires exactly 180×180 for high-DPI home screen.
    let apple = document.querySelector('link[rel="apple-touch-icon"][data-rgv-cal="1"]');
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      apple.href = '/admin-cal-icon-v2-180.png';
      apple.setAttribute('data-rgv-cal', '1');
      document.head.appendChild(apple);
    } else {
      // If an older href is cached, force-update so already-installed users
      // pick up the new icon on next launch.
      apple.href = '/admin-cal-icon-v2-180.png';
    }

    // 4) iOS standalone capability meta tags
    const iosMetas = [
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'apple-mobile-web-app-title', content: 'RGV Cal' },
      { name: 'mobile-web-app-capable', content: 'yes' },
    ];
    const createdMetas = [];
    iosMetas.forEach(({ name, content }) => {
      if (!document.querySelector(`meta[name="${name}"][data-rgv-cal="1"]`)) {
        const m = document.createElement('meta');
        m.name = name;
        m.content = content;
        m.setAttribute('data-rgv-cal', '1');
        document.head.appendChild(m);
        createdMetas.push(m);
      }
    });

    // 5) Register service worker (scope = /admin-cal/ so it only applies here)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/admin-cal-sw.js', { scope: '/admin-cal/' })
        .catch((err) => console.warn('Admin Cal SW registration failed:', err));
    }

    const prevTitle = document.title;
    document.title = 'RGV Calendar';

    return () => {
      // Best-effort cleanup if user navigates away
      try {
        if (link && link.parentNode) document.head.removeChild(link);
        if (meta && meta.parentNode) document.head.removeChild(meta);
        if (apple && apple.parentNode) document.head.removeChild(apple);
        createdMetas.forEach((m) => { if (m.parentNode) document.head.removeChild(m); });
      } catch (e) { /* noop */ }
      document.title = prevTitle;
    };
  }, [token]);

  // Capture Chrome's install prompt + detect if running as an installed PWA.
  useEffect(() => {
    // Standard PWA "is this an installed app launch" detection.
    // `display-mode: standalone` is true ONLY when launched as an installed PWA
    // (Chrome Custom Tabs report `display-mode: browser`, so this is safe).
    const checkStandalone = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIosStandalone = window.navigator.standalone === true;
      const isMinimalUi = window.matchMedia('(display-mode: minimal-ui)').matches;
      const isAndroidPwaLaunch = /^android-app:\/\//.test(document.referrer || '');
      if (isStandalone || isIosStandalone || isMinimalUi || isAndroidPwaLaunch) {
        setInstalled(true);
      }
    };
    checkStandalone();

    // Also listen for display-mode changes (e.g. user installs while page is open)
    const mq = window.matchMedia('(display-mode: standalone)');
    const onChange = (e) => { if (e.matches) setInstalled(true); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPromptEvt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setInstallPromptEvt(null);
      setInstalledPreviously(true);
      try { localStorage.setItem('rgv_cal_installed', '1'); } catch (e) { /* noop */ }
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const triggerInstall = async () => {
    if (installPromptEvt) {
      try {
        await installPromptEvt.prompt();
        await installPromptEvt.userChoice;
      } catch (e) { /* noop */ }
      setInstallPromptEvt(null);
      return;
    }
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    const isSamsungBrowser = /SamsungBrowser/i.test(ua);
    if (isIos || isSamsungBrowser) {
      setShowIosHint(true);
      return;
    }
    if (isAndroid) {
      alert(
        'To install:\n\n' +
        '1. Tap the ⋮ menu (three dots) in your Chrome browser\n' +
        '2. Tap "Add to Home screen" or "Install app"\n\n' +
        'Note: install only works in real Chrome, not WhatsApp/Samsung Internet.'
      );
      return;
    }
    // Desktop fallback
    alert(
      'To install on desktop:\n\n' +
      '1. Look at your Chrome address bar — you should see a small "Install" icon (computer with a down-arrow) on the right side\n' +
      '2. Click that icon → click "Install"\n\n' +
      'If you don\'t see the icon, try refreshing the page and waiting a few seconds.'
    );
  };

  // Validate token & register device. Also captures role for view-only enforcement.
  const [role, setRole] = useState('editor');
  const isViewer = role === 'viewer';
  // Real-time booking notifications
  const [newBookings, setNewBookings] = useState([]);
  const [dismissedBookings, setDismissedBookings] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('rgv-cal-dismissed-bookings') || '[]')); }
    catch { return new Set(); }
  });
  // Booking details cache (for day modal): { booking_id -> details | 'loading' | 'error' }
  const [bookingDetails, setBookingDetails] = useState({});

  // Web Push notifications state
  const [pushSupported] = useState(() =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
  const [pushStatus, setPushStatus] = useState('idle'); // idle | subscribed | denied | busy
  const [pushMsg, setPushMsg] = useState('');
  // Coupon generation (editor-only)
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponDraft, setCouponDraft] = useState({ discount_amount: '', guest_email: '', guest_phone: '', notes: '' });
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponResult, setCouponResult] = useState(null); // { code, discount_amount }
  const [couponErr, setCouponErr] = useState('');
  // Rate Management drawer (editors + viewers; viewers read-only)
  const [showRates, setShowRates] = useState(false);
  // B2B Agent register/manage drawer (editors only)
  const [showB2B, setShowB2B] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [b2bAgents, setB2bAgents] = useState([]);
  const [b2bForm, setB2bForm] = useState({ code: '', name: '', phone: '' });
  const [b2bErr, setB2bErr] = useState('');
  const [b2bShare, setB2bShare] = useState(null);
  const [b2bBusy, setB2bBusy] = useState(false);
  const loadB2BAgents = async () => {
    try {
      const r = await fetch(`${API}/api/admin-cal/${token}/b2b-agents`);
      if (r.ok) setB2bAgents(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { if (showB2B && token) loadB2BAgents(); // eslint-disable-next-line
  }, [showB2B]);
  const submitB2BAgent = async (e) => {
    e?.preventDefault?.();
    setB2bErr(''); setB2bShare(null); setB2bBusy(true);
    try {
      const r = await fetch(`${API}/api/admin-cal/${token}/b2b-agents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(b2bForm),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      setB2bShare(data);
      setB2bForm({ code: '', name: '', phone: '' });
      loadB2BAgents();
    } catch (err) {
      setB2bErr(err.message || 'Registration failed');
    } finally { setB2bBusy(false); }
  };
  const deleteB2BAgent = async (id, code) => {
    if (!window.confirm(`Deregister ${code}?`)) return;
    await fetch(`${API}/api/admin-cal/${token}/b2b-agents/${id}`, { method: 'DELETE' });
    loadB2BAgents();
  };
  useEffect(() => {
    const init = async () => {
      try {
        const v = await axios.get(`${API}/api/admin-cal/${token}/validate`);
        setRole(v.data?.role || 'editor');
        const deviceInfo = `${navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'} - ${navigator.platform || 'Unknown'}`;
        await axios.post(`${API}/api/admin-cal/${token}/register-device`, { device_id: getDeviceId(), device_info: deviceInfo });
        setValid(true);
      } catch (err) {
        setValid(false);
        setError(err.response?.data?.detail || 'Invalid or expired link');
      }
    };
    init();
  }, [token]);

  const fetchBlocked = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin-cal/${token}/blocked-dates`);
      setBlockedData(res.data);
    } catch {}
  }, [token]);

  useEffect(() => { if (valid) fetchBlocked(); }, [valid, fetchBlocked]);

  // Poll for new bookings every 30 seconds (and once immediately)
  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    const fetchRecent = async () => {
      try {
        const since = localStorage.getItem('rgv-cal-last-seen') ||
                      new Date(Date.now() - 24 * 3600 * 1000).toISOString();
        const res = await axios.get(`${API}/api/admin-cal/${token}/recent-bookings`, {
          params: { since },
        });
        if (cancelled) return;
        const fresh = (res.data?.bookings || []).filter(b => !dismissedBookings.has(b.id));
        setNewBookings(fresh);
      } catch {/* silent */}
    };
    fetchRecent();
    const interval = setInterval(fetchRecent, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [valid, token, dismissedBookings]);

  const handleDismissBooking = (bookingId) => {
    const next = new Set(dismissedBookings);
    next.add(bookingId);
    setDismissedBookings(next);
    localStorage.setItem('rgv-cal-dismissed-bookings', JSON.stringify([...next]));
    setNewBookings(prev => prev.filter(b => b.id !== bookingId));
  };

  const handleDismissAllBookings = () => {
    const next = new Set(dismissedBookings);
    newBookings.forEach(b => next.add(b.id));
    setDismissedBookings(next);
    localStorage.setItem('rgv-cal-dismissed-bookings', JSON.stringify([...next]));
    localStorage.setItem('rgv-cal-last-seen', new Date().toISOString());
    setNewBookings([]);
    // Clear app badge + title flash
    try { if ('clearAppBadge' in navigator) navigator.clearAppBadge(); } catch {/* noop */}
    document.title = 'RGV Calendar';
  };

  // Audio ping + tab title flash + app badge whenever a new booking appears
  useEffect(() => {
    const count = newBookings.length;
    if (count > prevBookingsCount.current && prevBookingsCount.current >= 0) {
      // Ring sound (synthesized — works offline, no asset needed)
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.34);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
        osc.start();
        osc.stop(ctx.currentTime + 0.65);
        if (navigator.vibrate) navigator.vibrate([200, 80, 200]);
      } catch {/* noop */}
    }
    prevBookingsCount.current = count;

    // Browser tab title flash + PWA app icon badge
    if (count > 0) {
      document.title = `(${count}) New Booking — RGV`;
      try { if ('setAppBadge' in navigator) navigator.setAppBadge(count); } catch {/* noop */}
    } else {
      document.title = 'RGV Calendar';
      try { if ('clearAppBadge' in navigator) navigator.clearAppBadge(); } catch {/* noop */}
    }
  }, [newBookings]);

  // Back button — keep the PWA alive instead of closing on system back
  useEffect(() => {
    // Push a sentinel state so the first back press lands here, not on app close
    if (typeof window !== 'undefined' && window.history.state?.rgvSentinel !== true) {
      window.history.pushState({ rgvSentinel: true }, '');
    }
    const onPop = () => {
      // If a modal is open, close that first. Else re-push sentinel.
      if (dayDetail) { setDayDetail(null); window.history.pushState({ rgvSentinel: true }, ''); return; }
      if (showModal) { setShowModal(false); window.history.pushState({ rgvSentinel: true }, ''); return; }
      // Otherwise show a "Tap back again to exit" prompt
      window.history.pushState({ rgvSentinel: true }, '');
      if (window.confirm('Exit RGV Calendar?')) {
        // Close PWA window (only works on installed PWA)
        try { window.close(); } catch {/* noop */}
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dayDetail, showModal]);

  const handleBackPress = () => {
    if (dayDetail) { setDayDetail(null); return; }
    if (showModal) { setShowModal(false); return; }
    if (window.confirm('Exit RGV Calendar?')) {
      try { window.close(); } catch {/* noop */}
      navigate('/');
    }
  };

  // Fetch booking details for blocks shown in the day-detail modal
  useEffect(() => {
    if (!dayDetail?.blocks?.length) return;
    const ids = dayDetail.blocks
      .map(b => b.booking_id)
      .filter(id => id && !bookingDetails[id]);
    if (ids.length === 0) return;
    ids.forEach(async (bid) => {
      setBookingDetails(prev => ({ ...prev, [bid]: 'loading' }));
      try {
        const res = await axios.get(`${API}/api/admin-cal/${token}/booking/${bid}`);
        setBookingDetails(prev => ({ ...prev, [bid]: res.data }));
      } catch {
        setBookingDetails(prev => ({ ...prev, [bid]: 'error' }));
      }
    });
  }, [dayDetail, token, bookingDetails]);

  // ============================================================
  // Web Push: read current subscription state and bind helpers
  // ============================================================
  useEffect(() => {
    if (!valid || !pushSupported) return;
    (async () => {
      try {
        if (Notification.permission === 'denied') {
          setPushStatus('denied');
          return;
        }
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushStatus(sub ? 'subscribed' : 'idle');
      } catch {/* noop */}
    })();
  }, [valid, pushSupported]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
  };

  const handleEnablePush = async () => {
    setPushMsg('');
    if (!pushSupported) {
      setPushMsg('Notifications are not supported on this device.');
      return;
    }
    setPushStatus('busy');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPushStatus(perm === 'denied' ? 'denied' : 'idle');
        setPushMsg(perm === 'denied' ? 'Notifications blocked. Enable in browser settings.' : '');
        return;
      }
      const keyRes = await axios.get(`${API}/api/admin-cal/push-key`);
      if (!keyRes.data?.enabled || !keyRes.data?.public_key) throw new Error('Server push not configured');

      const reg = await navigator.serviceWorker.ready;
      // Re-subscribe cleanly if a stale one exists
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.public_key),
      });
      await axios.post(`${API}/api/admin-cal/${token}/push-subscribe`, {
        subscription: sub.toJSON(),
        device_label: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop',
        user_agent: navigator.userAgent,
      });
      setPushStatus('subscribed');
      // Fire a test push so the user sees it works
      try { await axios.post(`${API}/api/admin-cal/${token}/push-test`); } catch {/* noop */}
      setPushMsg('Notifications enabled. Test ping sent.');
      setTimeout(() => setPushMsg(''), 4000);
    } catch (e) {
      setPushStatus('idle');
      setPushMsg('Could not enable notifications. Please try again.');
    }
  };

  const handleDisablePush = async () => {
    setPushMsg('');
    if (!pushSupported) return;
    setPushStatus('busy');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await axios.post(`${API}/api/admin-cal/${token}/push-unsubscribe`, { endpoint });
        } catch {/* noop */}
      }
      setPushStatus('idle');
      setPushMsg('Notifications turned off.');
      setTimeout(() => setPushMsg(''), 4000);
    } catch {
      setPushStatus('subscribed');
      setPushMsg('Could not turn off. Try again.');
    }
  };

  const handleBlock = async () => {
    if (!selectedDate || !selectedVilla) return;
    const start = toDateStr(selectedDate);
    const end = endDate || toDateStr(new Date(selectedDate.getTime() + 86400000));
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin-cal/${token}/block`, {
        villa_id: selectedVilla,
        start_date: start,
        end_date: end,
        reason: reason || 'Offline booking',
      });
      await fetchBlocked();
      setShowModal(false);
      setReason('');
      setEndDate('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to block');
    }
    setLoading(false);
  };

  const handleUnblock = async (blockId) => {
    if (!window.confirm('Remove this block?')) return;
    try {
      await axios.delete(`${API}/api/admin-cal/${token}/unblock/${blockId}`);
      await fetchBlocked();
      setDayDetail(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Cannot remove Airbnb synced blocks');
    }
  };

  // Calendar rendering
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentMonth.toLocaleString('en-IN', { month: 'long', year: 'numeric' });

  const getBlocksForDay = (day) => {
    const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const blocks = [];
    for (const vid of VILLAS.map(v => v.id)) {
      for (const b of (blockedData[vid] || [])) {
        if (b.start_date <= ds && ds < b.end_date) {
          blocks.push({ ...b, villa_id: vid });
        }
      }
    }
    return blocks;
  };

  if (valid === null) return (
    <div className="min-h-screen bg-[#1A1A2E] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-3 border-[#F57C20] border-t-transparent rounded-full" />
    </div>
  );

  if (!valid) return (
    <div className="min-h-screen bg-[#1A1A2E] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
        <FiLock className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#1A1A2E] mb-2">Access Denied</h2>
        <p className="text-[#6B7280] text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="admin-cal-page">
      {/* Header */}
      <div className="bg-[#1A1A2E] text-white px-3 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-2">
          <button
            onClick={handleBackPress}
            data-testid="cal-back-btn"
            aria-label="Back"
            className="w-9 h-9 rounded-full bg-white/10 active:bg-white/20 flex items-center justify-center flex-shrink-0"
          >
            <FiArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base font-bold tracking-tight flex-1 truncate">RGV Calendar</h1>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {newBookings.length > 0 && (
              <button
                onClick={() => {
                  // Scroll to new bookings banner
                  document.querySelector('[data-testid="new-bookings-banner"]')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="cal-bell-btn"
                aria-label="New bookings"
                className="relative w-9 h-9 rounded-full bg-[#F57C20] flex items-center justify-center animate-pulse"
              >
                <FiBell className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#1A1A2E]">
                  {newBookings.length > 9 ? '9+' : newBookings.length}
                </span>
              </button>
            )}
            {!isViewer && (
              <button
                onClick={() => { setCouponResult(null); setCouponErr(''); setCouponDraft({ discount_amount: '', guest_email: '', guest_phone: '', notes: '' }); setShowCoupon(true); }}
                data-testid="cal-coupon-btn"
                aria-label="Generate coupon"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
                title="Generate coupon"
              >
                <FiTag className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setShowRates(true)}
              data-testid="cal-rates-btn"
              aria-label="Rate management"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
              title="Rate management"
            >
              <FiDollarSign className="w-4 h-4" />
            </button>
            {!isViewer && (
              <button
                onClick={() => setShowB2B(true)}
                data-testid="cal-b2b-btn"
                aria-label="B2B Agents"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
                title="B2B Agents"
              >
                <FiUserPlus className="w-4 h-4" />
              </button>
            )}
            {!isViewer && (
              <button
                onClick={() => setShowQuote(true)}
                data-testid="cal-quote-btn"
                aria-label="Generate quotation"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
                title="Generate quotation"
              >
                <FiFileText className="w-4 h-4" />
              </button>
            )}
            {isViewer && (
              <span data-testid="viewer-badge" className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-400/30">
                Viewer
              </span>
            )}
          </div>
        </div>
        {/* Villa colour legend on its own row to keep header tidy */}
        <div className="max-w-lg mx-auto flex gap-1 mt-2 overflow-x-auto hide-scrollbar">
          {VILLAS.map(v => (
            <span key={v.id} className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: v.color, color: '#fff' }}>{v.name}</span>
          ))}
        </div>
      </div>

      {/* Push Notifications Toggle */}
      {pushSupported && (
        <div className="bg-white border-b border-gray-100 px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#1A1A2E]">
                {pushStatus === 'subscribed' ? '🔔 Notifications ON' :
                 pushStatus === 'denied' ? '🚫 Notifications blocked' :
                 '🔕 Notifications OFF'}
              </p>
              <p className="text-[10px] text-[#6B7280]">
                {pushStatus === 'subscribed' ? 'You\'ll be alerted instantly when a booking arrives.' :
                 pushStatus === 'denied' ? 'Re-enable from your browser settings.' :
                 'Tap "Enable" to get pinged the moment a booking lands.'}
              </p>
              {pushMsg && <p className="text-[10px] text-[#F57C20] mt-0.5 font-semibold">{pushMsg}</p>}
            </div>
            {pushStatus === 'subscribed' ? (
              <button
                onClick={handleDisablePush}
                data-testid="disable-push-btn"
                disabled={pushStatus === 'busy'}
                className="text-[10px] px-3 py-1.5 rounded-full bg-gray-100 text-[#1A1A2E] font-bold border border-gray-200 hover:bg-gray-200 disabled:opacity-50 flex-shrink-0"
              >
                Turn Off
              </button>
            ) : pushStatus === 'denied' ? null : (
              <button
                onClick={handleEnablePush}
                data-testid="enable-push-btn"
                disabled={pushStatus === 'busy'}
                className="text-[10px] px-3 py-1.5 rounded-full bg-[#F57C20] text-white font-bold disabled:opacity-50 flex-shrink-0"
              >
                {pushStatus === 'busy' ? 'Working...' : 'Enable'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* New Booking Notifications */}
      {newBookings.length > 0 && (
        <div data-testid="new-bookings-banner" className="bg-gradient-to-r from-[#F57C20]/10 to-[#F57C20]/5 border-b-2 border-[#F57C20]/40 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-[#F57C20] text-xs font-bold uppercase tracking-wider">
                🔔 {newBookings.length} New Booking{newBookings.length > 1 ? 's' : ''}
              </p>
              <button
                onClick={handleDismissAllBookings}
                data-testid="dismiss-all-bookings"
                className="text-[10px] text-[#6B7280] hover:text-[#1A1A2E] underline"
              >
                Mark all as read
              </button>
            </div>
            <div className="space-y-1.5">
              {newBookings.slice(0, 3).map(b => (
                <div key={b.id} data-testid={`booking-notif-${b.id.slice(0, 8)}`} className="bg-white border border-[#F57C20]/20 rounded-lg p-2.5 shadow-sm">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#1A1A2E] truncate">{b.user_name || 'Guest'}</p>
                      <p className="text-[11px] text-[#6B7280] truncate">{b.villa_name_display}</p>
                    </div>
                    <button
                      onClick={() => handleDismissBooking(b.id)}
                      data-testid={`dismiss-booking-${b.id.slice(0, 8)}`}
                      className="text-[#6B7280] hover:text-[#1A1A2E] text-[16px] leading-none px-1"
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[10px] text-[#374151]">
                    <span>📅 {b.check_in} → {b.check_out}</span>
                    <span>👥 {b.adults || 0}A {b.kids ? `${b.kids}K` : ''}</span>
                    <span>💰 Rs.{(b.grand_total || 0).toLocaleString()}</span>
                    <span className="text-[#F57C20] font-semibold">#{b.booking_ref}</span>
                  </div>
                  {b.user_phone && (
                    <a
                      href={`https://wa.me/91${b.user_phone.replace(/\D/g, '').slice(-10)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-1.5 text-[10px] text-[#25D366] hover:underline font-semibold"
                    >
                      WhatsApp Guest →
                    </a>
                  )}
                </div>
              ))}
              {newBookings.length > 3 && (
                <p className="text-center text-[10px] text-[#6B7280] italic">+{newBookings.length - 3} more</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Browser advice banner — shown for in-app browsers (WhatsApp/Instagram/FB), Samsung Internet, etc. */}
      {browserAdvice && !browserAdviceDismissed && !installed && (
        <div className="bg-red-50 border-b-2 border-red-300 text-[#1A1A2E] px-4 py-3" data-testid="admin-cal-browser-advice">
          <div className="max-w-lg mx-auto flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight mb-1">
                {browserAdvice.type === 'inapp'
                  ? `Open in Chrome to install the app`
                  : browserAdvice.type === 'samsung'
                  ? `Open in Chrome (Samsung Internet can't install this app cleanly)`
                  : `Open in Chrome to install the app`}
              </p>
              <p className="text-[11px] text-[#6B7280] leading-relaxed">
                {browserAdvice.type === 'inapp'
                  ? `You opened this from ${browserAdvice.label}. PWA install only works in Chrome on Android.`
                  : browserAdvice.type === 'samsung'
                  ? `Samsung Internet may flag the installed app as "unsafe". Chrome installs cleanly.`
                  : `For best results install via Google Chrome.`}
              </p>
              <button
                onClick={openInChrome}
                data-testid="admin-cal-open-in-chrome-btn"
                className="mt-2 inline-flex items-center gap-1 bg-[#F57C20] hover:bg-[#E06A10] active:bg-[#C75A05] transition-colors text-white text-xs font-bold px-4 py-2 rounded-full"
              >
                Open in Chrome →
              </button>
            </div>
            <button
              onClick={dismissBrowserAdvice}
              data-testid="admin-cal-browser-advice-dismiss"
              aria-label="Dismiss"
              className="text-[#9CA3AF] hover:text-[#1A1A2E] flex-shrink-0 p-1"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Prominent install banner (hidden once app is installed or user dismisses) */}
      {!installed && !installDismissed && (
        <div className="bg-gradient-to-r from-[#F57C20] to-[#E06A10] text-white px-4 py-3 shadow-md" data-testid="admin-cal-install-banner">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                <FiDownload className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                {installedPreviously ? (
                  <>
                    <p className="text-sm font-bold leading-tight">RGV Cal is installed on this device</p>
                    <p className="text-[11px] text-white/85 leading-tight">Find it in your Start Menu / Apps, or tap the 📱 icon in your address bar.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold leading-tight">Install RGV Cal App</p>
                    <p className="text-[11px] text-white/80 leading-tight">Tap once — opens like a real app, no browser needed.</p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!installedPreviously && (
                <button
                  onClick={triggerInstall}
                  data-testid="admin-cal-install-btn"
                  className="bg-white text-[#F57C20] text-xs font-bold px-4 py-2 rounded-full hover:bg-white/90 active:bg-white/80 transition-colors whitespace-nowrap"
                >
                  Install
                </button>
              )}
              <button
                onClick={dismissInstall}
                data-testid="admin-cal-install-dismiss"
                aria-label="Dismiss"
                className="p-1.5 text-white/70 hover:text-white transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Install hint (iOS Safari or Samsung Internet) */}
      {showIosHint && (
        <div className="bg-[#F57C20]/10 border-b border-[#F57C20]/30 text-[#1A1A2E] px-4 py-3" data-testid="admin-cal-ios-hint">
          <div className="max-w-lg mx-auto flex items-start justify-between gap-3">
            <div className="text-xs leading-relaxed">
              {/SamsungBrowser/i.test(navigator.userAgent) ? (
                <>
                  <p className="mb-1"><strong>Samsung Internet doesn&apos;t install PWAs reliably</strong> (you may see &ldquo;Unsafe app blocked&rdquo;).</p>
                  <p>👉 Open this link in <strong>Google Chrome</strong> (or Edge), then tap <strong>Install</strong> again. Chrome installs the app cleanly without warnings.</p>
                </>
              ) : (
                <p><strong>To install on iPhone:</strong> tap the <strong>Share</strong> icon (square with arrow up) in Safari, then choose <strong>&ldquo;Add to Home Screen&rdquo;</strong>.</p>
              )}
            </div>
            <button onClick={() => setShowIosHint(false)} className="text-[#1A1A2E] hover:text-[#F57C20] flex-shrink-0" data-testid="admin-cal-ios-hint-close">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Airbnb Sync Health */}
      <div className="max-w-lg mx-auto px-4 pt-3">
        <SyncHealthWidget
          fetchHealth={() => axios.get(`${API}/api/admin-cal/${token}/ical/health`)}
          triggerSync={isViewer ? undefined : (vid) => axios.post(`${API}/api/admin-cal/${token}/ical/sync/${vid}`)}
          copyUrlBaseOverride="https://royalgardenvillas.in"
          compact={true}
        />
      </div>

      {/* Month Nav */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} className="p-2 rounded-full hover:bg-gray-200 active:bg-gray-300"><FiChevronLeft className="w-5 h-5" /></button>
          <h2 className="text-lg font-bold text-[#1A1A2E]">{monthName}</h2>
          <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} className="p-2 rounded-full hover:bg-gray-200 active:bg-gray-300"><FiChevronRight className="w-5 h-5" /></button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} className="text-center text-[10px] font-semibold text-[#9CA3AF] uppercase py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const blocks = getBlocksForDay(day);
            const isToday = toDateStr(new Date()) === `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            return (
              <button
                key={day}
                data-testid={`cal-day-${day}`}
                onClick={() => {
                  const d = new Date(year, month, day);
                  if (blocks.length > 0) {
                    // Dedup 12BHK auto-blocks: when 3/4/5BHK are individually booked,
                    // the 12BHK gets multiple "Auto-blocked" entries — collapse to a single
                    // synthetic entry so the manager sees one clean row.
                    const dedupedBlocks = (() => {
                      const twelveAuto = blocks.filter(b =>
                        b.villa_id === '12bhk-celebration-villa-lonavala' &&
                        /Auto-blocked|booked/i.test(b.reason || '')
                      );
                      const others = blocks.filter(b => !twelveAuto.includes(b));
                      if (twelveAuto.length === 0) return blocks;
                      const synth = {
                        ...twelveAuto[0],
                        id: `synth-12bhk-${twelveAuto.map(x => x.id).join('-')}`,
                        reason: 'Blocked due to other villa booking',
                        booking_id: null,           // hide enriched lookup
                        source: 'auto_overlap',
                        _synthetic: true,
                      };
                      return [...others, synth];
                    })();
                    setDayDetail({ day, date: d, blocks: dedupedBlocks });
                  } else if (!isViewer) {
                    setSelectedDate(d);
                    setShowModal(true);
                  }
                }}
                className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-medium transition-all active:scale-95
                  ${isToday ? 'ring-2 ring-[#F57C20]' : ''}
                  ${blocks.length > 0 ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}
                `}
              >
                <span className={blocks.length > 0 ? 'text-[#1A1A2E] font-bold' : 'text-[#6B7280]'}>{day}</span>
                {blocks.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {VILLAS.map(v => {
                      const has = blocks.some(b => b.villa_id === v.id);
                      return has ? <div key={v.id} className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} /> : null;
                    })}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3 justify-center pb-2">
          {VILLAS.map(v => (
            <div key={v.id} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
              <span className="text-[10px] text-[#6B7280] font-medium">{v.name}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-[10px] text-[#6B7280] font-medium">Tap empty = block</span>
          </div>
        </div>
      </div>

      {/* Day Detail Modal */}
      {dayDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setDayDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-[#1A1A2E]">
                {dayDetail.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </h3>
              <button onClick={() => setDayDetail(null)} className="text-[#9CA3AF]"><FiX className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 mb-4">
              {dayDetail.blocks.map(b => {
                const v = VILLAS.find(x => x.id === b.villa_id);
                const details = b.booking_id ? bookingDetails[b.booking_id] : null;
                const isLoading = details === 'loading';
                const isError = details === 'error';
                const d = (details && details !== 'loading' && details !== 'error') ? details : null;
                return (
                  <div key={b.id} className="rounded-xl overflow-hidden" style={{ background: v?.bg }}>
                    <div className="flex items-start justify-between p-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className="text-xs font-bold" style={{ color: v?.color }}>{v?.name}</span>
                          {b.source === 'airbnb' && <span className="text-[9px] text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded">Airbnb</span>}
                          {b.source === 'booking' && <span className="text-[9px] text-green-700 bg-green-100 px-1.5 py-0.5 rounded font-bold">Website</span>}
                          {b.source === 'admin_manual' && <span className="text-[9px] text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded font-bold">Manual</span>}
                          {b._synthetic && <span className="text-[9px] text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded font-bold">Auto</span>}
                        </div>
                        {!d && <p className="text-xs text-[#6B7280]">{b.reason}</p>}
                        <p className="text-[10px] text-[#9CA3AF]">{b.start_date} → {b.end_date}</p>
                      </div>
                      {!isViewer && !b._synthetic && (
                        <button onClick={() => handleUnblock(b.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0" data-testid={`unblock-${b.id}`}>
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Enriched booking details (website bookings only) */}
                    {b.booking_id && (
                      <div className="bg-white/70 backdrop-blur-sm border-t border-white/60 px-3 py-2.5 text-[11px]">
                        {isLoading && <p className="text-[#9CA3AF] italic">Loading booking details...</p>}
                        {isError && <p className="text-red-500 italic">Could not load booking details</p>}
                        {d && (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="font-bold text-[#1A1A2E] truncate" data-testid={`guest-name-${b.id}`}>
                                {d.guest_name}
                                <span className="text-[#9CA3AF] font-normal ml-1.5">#{d.booking_ref}</span>
                              </p>
                              <span className="text-[9px] text-[#9CA3AF]">{d.total_guests} guest{d.total_guests !== 1 ? 's' : ''}</span>
                            </div>
                            {d.guest_phone && (
                              <div className="flex items-center gap-2 mb-2">
                                <a
                                  href={`tel:${d.guest_phone}`}
                                  className="text-[#1A1A2E] hover:text-[#F57C20]"
                                >
                                  📞 {d.guest_phone}
                                </a>
                                <a
                                  href={`https://wa.me/91${(d.guest_phone || '').replace(/\D/g, '').slice(-10)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#25D366] font-semibold ml-auto"
                                >
                                  WhatsApp →
                                </a>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-1.5">
                              <div className="bg-[#F7F8FA] rounded p-1.5">
                                <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Total</p>
                                <p className="text-[11px] font-bold text-[#1A1A2E]">Rs.{(d.grand_total || 0).toLocaleString()}</p>
                                <p className="text-[8px] text-[#9CA3AF]">incl. deposit</p>
                              </div>
                              <div className="bg-green-50 rounded p-1.5">
                                <p className="text-[9px] text-green-700/70 uppercase tracking-wider">Received</p>
                                <p className="text-[11px] font-bold text-green-700">Rs.{(d.amount_paid || 0).toLocaleString()}</p>
                              </div>
                              <div className={`rounded p-1.5 ${d.outstanding > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                                <p className={`text-[9px] uppercase tracking-wider ${d.outstanding > 0 ? 'text-[#F57C20]/70' : 'text-green-700/70'}`}>Outstanding</p>
                                <p className={`text-[11px] font-bold ${d.outstanding > 0 ? 'text-[#F57C20]' : 'text-green-700'}`}>Rs.{(d.outstanding || 0).toLocaleString()}</p>
                              </div>
                            </div>
                            {d.pricing_breakdown && (
                              <details className="mt-2 bg-white/80 rounded border border-gray-200" data-testid={`pricing-breakdown-${b.id}`}>
                                <summary className="cursor-pointer text-[10px] font-bold text-[#1A1A2E] uppercase tracking-wider px-2 py-1.5 select-none">
                                  Pricing breakdown
                                </summary>
                                <div className="px-2 pb-2 pt-1 space-y-0.5 text-[10px]">
                                  {(d.pricing_breakdown.nightly_details || []).map((nd, idx) => {
                                    const orig = Number(nd.original_rate) || 0;
                                    const saved = Number(nd.saved) || 0;
                                    const pct = nd.discount_applied && orig ? Math.round((saved * 100) / orig) : 0;
                                    return (
                                      <div key={idx} className="flex justify-between gap-2">
                                        <span className="text-[#4B5563] truncate">
                                          Night {nd.night} · {nd.date}
                                          {nd.discount_applied && <span className="text-green-700 ml-1">({pct}% off)</span>}
                                        </span>
                                        <span className="font-semibold text-[#1A1A2E] flex-shrink-0">Rs.{(nd.final_rate || 0).toLocaleString()}</span>
                                      </div>
                                    );
                                  })}
                                  {(d.pricing_breakdown.nightly_details?.length || 0) > 0 && !d.pricing_breakdown.historical_pricing && (
                                    <div className="flex justify-between gap-2 border-t border-gray-200 pt-1 mt-1 font-semibold">
                                      <span className="text-[#374151]">Room subtotal</span>
                                      <span className="text-[#1A1A2E]">Rs.{(d.pricing_breakdown.nights_subtotal || 0).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.historical_pricing && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563] truncate">
                                        Room ({d.pricing_breakdown.nights} night{d.pricing_breakdown.nights !== 1 ? 's' : ''})
                                        <span className="text-[#9CA3AF] ml-1">(historical pricing)</span>
                                      </span>
                                      <span className="font-semibold text-[#1A1A2E] flex-shrink-0">Rs.{(d.pricing_breakdown.nights_subtotal || 0).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.extra_charges > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563]">Extra guests ({d.pricing_breakdown.extra_guests})</span>
                                      <span className="font-semibold text-[#1A1A2E]">Rs.{d.pricing_breakdown.extra_charges.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.maid_charge > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563]">Maid ({d.pricing_breakdown.maid_days}d)</span>
                                      <span className="font-semibold text-[#1A1A2E]">Rs.{d.pricing_breakdown.maid_charge.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.bbq_charge > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563]">BBQ ({d.pricing_breakdown.bbq_meals})</span>
                                      <span className="font-semibold text-[#1A1A2E]">Rs.{d.pricing_breakdown.bbq_charge.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.bonfire_charge > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563]">Bonfire ({d.pricing_breakdown.bonfire_sessions})</span>
                                      <span className="font-semibold text-[#1A1A2E]">Rs.{d.pricing_breakdown.bonfire_charge.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.security_deposit > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-[#4B5563]">Deposit (refundable)</span>
                                      <span className="font-semibold text-[#1A1A2E]">Rs.{d.pricing_breakdown.security_deposit.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {d.pricing_breakdown.coupon_discount > 0 && (
                                    <div className="flex justify-between gap-2">
                                      <span className="text-green-700 font-semibold">🎟️ Coupon ({d.pricing_breakdown.coupon_code || 'applied'})</span>
                                      <span className="font-bold text-green-700">– Rs.{d.pricing_breakdown.coupon_discount.toLocaleString()}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between gap-2 border-t-2 border-[#1A1A2E] pt-1 mt-1 font-bold">
                                    <span className="text-[#1A1A2E]">Grand Total</span>
                                    <span className="text-[#1A1A2E]">Rs.{(d.pricing_breakdown.grand_total || 0).toLocaleString()}</span>
                                  </div>
                                </div>
                              </details>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {!isViewer ? (
              <button
                onClick={() => { setSelectedDate(dayDetail.date); setDayDetail(null); setShowModal(true); }}
                className="w-full bg-[#F57C20] text-white py-3 rounded-xl text-sm font-semibold"
              >
                + Block Another Villa
              </button>
            ) : (
              <p className="text-center text-[11px] text-[#9CA3AF] italic py-2">
                You have view-only access — contact the admin to block / unblock dates.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Block Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-[#1A1A2E]">Block Dates</h3>
              <button onClick={() => setShowModal(false)} className="text-[#9CA3AF]"><FiX className="w-5 h-5" /></button>
            </div>

            <p className="text-xs text-[#6B7280] mb-3">
              Start: <span className="font-semibold text-[#1A1A2E]">{selectedDate?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </p>

            <label className="text-[10px] font-semibold uppercase text-[#6B7280] block mb-1">Villa</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {VILLAS.map(v => (
                <button
                  key={v.id}
                  data-testid={`block-villa-${v.id}`}
                  onClick={() => setSelectedVilla(v.id)}
                  className={`py-2 rounded-lg text-xs font-bold transition-all ${selectedVilla === v.id ? 'text-white shadow-md' : 'text-[#6B7280] bg-gray-100'}`}
                  style={selectedVilla === v.id ? { background: v.color } : {}}
                >
                  {v.name}
                </button>
              ))}
            </div>

            <label className="text-[10px] font-semibold uppercase text-[#6B7280] block mb-1">End Date (checkout)</label>
            <input
              type="date"
              value={endDate}
              min={selectedDate ? toDateStr(new Date(selectedDate.getTime() + 86400000)) : ''}
              onChange={e => setEndDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-[#F57C20]"
            />

            <label className="text-[10px] font-semibold uppercase text-[#6B7280] block mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Offline booking - Mr. Sharma"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mb-4 focus:outline-none focus:border-[#F57C20]"
            />

            <button
              data-testid="block-confirm-btn"
              onClick={handleBlock}
              disabled={loading}
              className="w-full bg-[#F57C20] text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <FiCheck className="w-4 h-4" />}
              Block Dates
            </button>
          </div>
        </div>
      )}

      {/* COUPON GENERATION MODAL (editor only) */}
      {showCoupon && !isViewer && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-3" data-testid="coupon-modal">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#F57C20]/10 flex items-center justify-center">
                  <FiTag className="w-4 h-4 text-[#F57C20]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A1A2E]">Generate Coupon</p>
                  <p className="text-[10px] text-[#6B7280]">Single-use discount code</p>
                </div>
              </div>
              <button onClick={() => setShowCoupon(false)} data-testid="coupon-close" className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <FiX className="w-4 h-4 text-[#6B7280]" />
              </button>
            </div>

            {couponResult ? (
              <div className="p-5 text-center" data-testid="coupon-success">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <FiCheck className="w-7 h-7 text-green-600" />
                </div>
                <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">Coupon created</p>
                <div className="bg-gradient-to-br from-[#F57C20]/10 to-[#F57C20]/5 border-2 border-dashed border-[#F57C20]/40 rounded-xl p-4 my-3">
                  <p className="text-2xl font-black tracking-widest text-[#1A1A2E]" style={{ fontFamily: 'monospace' }}>{couponResult.code}</p>
                  <p className="text-sm text-[#F57C20] font-bold mt-1">Rs. {couponResult.discount_amount?.toLocaleString()} OFF</p>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(couponResult.code); }}
                  data-testid="coupon-copy"
                  className="w-full bg-[#1A1A2E] text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 mb-2"
                >
                  <FiCopy className="w-3.5 h-3.5" /> Copy Code
                </button>
                <button
                  onClick={() => { setCouponResult(null); setCouponDraft({ discount_amount: '', guest_email: '', guest_phone: '', notes: '' }); }}
                  data-testid="coupon-new"
                  className="w-full text-[#F57C20] py-2 rounded-lg text-xs font-semibold border border-[#F57C20]/30 hover:bg-[#F57C20]/5"
                >
                  + Create another
                </button>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Discount amount (₹) *</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 500"
                    value={couponDraft.discount_amount}
                    onChange={e => setCouponDraft(d => ({ ...d, discount_amount: e.target.value }))}
                    data-testid="coupon-amount-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:border-[#F57C20]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Guest email <span className="text-gray-400 normal-case font-normal">(optional — lock to this guest)</span></label>
                  <input
                    type="email"
                    placeholder="guest@example.com"
                    value={couponDraft.guest_email}
                    onChange={e => setCouponDraft(d => ({ ...d, guest_email: e.target.value }))}
                    data-testid="coupon-email-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:border-[#F57C20]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Guest phone <span className="text-gray-400 normal-case font-normal">(optional)</span></label>
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="+91 98200 88292"
                    value={couponDraft.guest_phone}
                    onChange={e => setCouponDraft(d => ({ ...d, guest_phone: e.target.value }))}
                    data-testid="coupon-phone-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:border-[#F57C20]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Notes <span className="text-gray-400 normal-case font-normal">(optional)</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Repeat guest goodwill"
                    value={couponDraft.notes}
                    onChange={e => setCouponDraft(d => ({ ...d, notes: e.target.value }))}
                    data-testid="coupon-notes-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:border-[#F57C20]"
                  />
                </div>

                {couponErr && (
                  <p data-testid="coupon-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">{couponErr}</p>
                )}

                <button
                  onClick={async () => {
                    setCouponErr('');
                    const amt = parseInt(couponDraft.discount_amount, 10);
                    if (!Number.isFinite(amt) || amt <= 0) { setCouponErr('Enter a valid discount amount'); return; }
                    setCouponBusy(true);
                    try {
                      const res = await axios.post(`${API}/api/admin-cal/${token}/coupons`, {
                        discount_amount: amt,
                        guest_email: couponDraft.guest_email || undefined,
                        guest_phone: couponDraft.guest_phone || undefined,
                        notes: couponDraft.notes || undefined,
                      });
                      setCouponResult(res.data);
                    } catch (err) {
                      setCouponErr(err.response?.data?.detail || 'Failed to create coupon');
                    }
                    setCouponBusy(false);
                  }}
                  disabled={couponBusy}
                  data-testid="coupon-submit"
                  className="w-full bg-[#F57C20] text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                >
                  {couponBusy ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <FiTag className="w-4 h-4" />}
                  Generate Coupon
                </button>
                <p className="text-[10px] text-center text-[#9CA3AF]">Existing bookings are unaffected. Code is single-use only.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* B2B Agent drawer (editors only) */}
      {showB2B && (
        <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowB2B(false)} data-testid="cal-b2b-drawer">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto">
            <div className="bg-[#1B2432] text-white px-5 py-4 flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-base font-semibold flex items-center gap-2"><FiUserPlus className="w-4 h-4 text-[#F57C20]" /> B2B Agents</h3>
              <button onClick={() => setShowB2B(false)} className="text-white/60 hover:text-white"><FiX className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <form onSubmit={submitB2BAgent} className="bg-[#F7F8FA] border border-gray-200 rounded-xl p-4 mb-4">
                <p className="text-[#1A1A2E] text-sm font-semibold mb-3 flex items-center gap-2"><FiPlus className="w-4 h-4 text-[#F57C20]" /> Register New Agent</p>
                <div className="space-y-2">
                  <input data-testid="cal-b2b-code" placeholder="Agent Code (e.g. RAHUL01)" value={b2bForm.code}
                    onChange={(e) => setB2bForm({ ...b2bForm, code: e.target.value.toUpperCase() })}
                    className="w-full bg-white border border-gray-200 text-[#1A1A2E] text-sm rounded-lg px-3 py-2 uppercase focus:outline-none focus:border-[#F57C20]" />
                  <input data-testid="cal-b2b-name" placeholder="Agent / Agency Name" value={b2bForm.name}
                    onChange={(e) => setB2bForm({ ...b2bForm, name: e.target.value })}
                    className="w-full bg-white border border-gray-200 text-[#1A1A2E] text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#F57C20]" />
                  <input data-testid="cal-b2b-phone" placeholder="Mobile (10 digits)" value={b2bForm.phone} inputMode="numeric"
                    onChange={(e) => setB2bForm({ ...b2bForm, phone: e.target.value })}
                    className="w-full bg-white border border-gray-200 text-[#1A1A2E] text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#F57C20]" />
                </div>
                {b2bErr && <p className="text-red-600 text-xs mt-2">{b2bErr}</p>}
                <button type="submit" disabled={b2bBusy} data-testid="cal-b2b-submit"
                  className="mt-3 w-full bg-[#F57C20] hover:bg-[#E06A10] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
                  {b2bBusy ? 'Registering…' : 'Register Agent'}
                </button>
              </form>
              {b2bShare && (
                <div data-testid="cal-b2b-share" className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                  <p className="text-emerald-700 text-xs font-semibold mb-2">✅ Registered — share with your agent</p>
                  <pre className="whitespace-pre-wrap text-[#1A1A2E] text-[11px] bg-white rounded p-2 border font-mono">{b2bShare.share_message}</pre>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => navigator.clipboard.writeText(b2bShare.share_message)} className="bg-[#1A1A2E] text-white text-[10px] px-2.5 py-1 rounded">Copy</button>
                    <a href={`https://wa.me/91${b2bShare.agent.phone}?text=${encodeURIComponent(b2bShare.share_message)}`} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white text-[10px] px-2.5 py-1 rounded">WhatsApp</a>
                    <button onClick={() => setB2bShare(null)} className="text-[#6B7280] text-[10px]">Dismiss</button>
                  </div>
                </div>
              )}
              <p className="text-[#6B7280] text-[11px] font-semibold uppercase tracking-wider mb-2">Registered Agents ({b2bAgents.length})</p>
              {b2bAgents.length === 0 ? (
                <p className="text-[#9CA3AF] text-xs">No agents yet.</p>
              ) : (
                <div className="space-y-2">
                  {b2bAgents.map(a => (
                    <div key={a.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-[#1A1A2E] text-sm font-semibold truncate">{a.code} <span className="text-[#6B7280] font-normal">· {a.name}</span></p>
                        <p className="text-[#9CA3AF] text-[11px]">+91 {a.phone}</p>
                      </div>
                      <button onClick={() => deleteB2BAgent(a.id, a.code)} className="text-[#9CA3AF] hover:text-red-500 p-1">
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!isViewer && (
        <QuoteModal open={showQuote} onClose={() => setShowQuote(false)} mode="cal" token={token} title="Generate Quotation" />
      )}

      {/* Rate Management drawer */}
      {showRates && (
        <AdminCalRatesPanel
          token={token}
          role={role}
          onClose={() => setShowRates(false)}
        />
      )}
    </div>
  );
};

export default AdminCalPage;
